import { useState, useEffect } from 'react';

export interface ViewportInfo {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isEmbedded: boolean;
  orientation: 'portrait' | 'landscape';
}

export const useResponsiveIframe = () => {
  const [viewportInfo, setViewportInfo] = useState<ViewportInfo>(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    return {
      width,
      height,
      isMobile: width < 640,
      isTablet: width >= 640 && width < 1024,
      isDesktop: width >= 1024,
      isEmbedded: window.self !== window.top,
      orientation: width > height ? 'landscape' : 'portrait'
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setViewportInfo({
        width,
        height,
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024,
        isEmbedded: window.self !== window.top,
        orientation: width > height ? 'landscape' : 'portrait'
      });

      // Send size info to parent if embedded
      if (window.self !== window.top) {
        window.parent.postMessage({
          type: 'IFRAME_RESIZE',
          payload: {
            width,
            height,
            isMobile: width < 640,
            orientation: width > height ? 'landscape' : 'portrait'
          }
        }, '*');
      }
    };

    // Initial call
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    
    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', handleResize);

    // Listen for messages from parent to force resize
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'FORCE_RESIZE') {
        handleResize();
      }
    };
    
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return viewportInfo;
};

export default useResponsiveIframe;